import { elemBind } from 'utilities/elem.js';
import { seqId } from 'utilities/seqid.js';
import {
  findClosestAssetByQuality,
  numericSizeSnapped,
  withinQualityRange,
  nearestOutsideRange,
} from 'utilities/assets.js';
import { globalEventLoop } from 'utilities/event_loop.js';
import { count, sample } from 'utilities/metrics.js';
import { parse as parseHls, setOptions as setOptionsHls } from 'hls-parser';
import defineEngine from '../defineEngine.js';
import { selectable } from './asset_selection.js';
import * as HlsAssets from '../hls_video/hls_assets.js';
import SimpleVideo from '../simple_video/index.js';
import { fetchMediaData } from '../../../../../../utilities/fetchMediaData.ts';
import { PlaybackEngine } from '../PlaybackEngine.ts';

const delegatePublicMethods = SimpleVideo.delegatePublicMethods;
const mediaDataWithAssets = SimpleVideo.mediaDataWithAssets;

class NativeHlsVideo extends PlaybackEngine {
  #firstFragProgramDateTime;

  #liveStreamStartedAt;

  #liveStreamM3u8RenditionProgramTime;

  constructor(root, mediaData, attributes, otherEngine) {
    super(root, mediaData, attributes, otherEngine);
    this.uuid = seqId('wistia_native_hls_video_');
    this.root = root;
    this.name = 'NativeHlsVideo';

    this.mediaData = mediaData;
    this.allAssets = this.mediaData.assets;
    this.audioAssets = HlsAssets.allHlsAudioAssets(this.allAssets);
    this.attributes = attributes || {};
    this.unbinds = [];
    this._currentAudioTrackId = 0;
    this.videoWidth = 0;

    this.simpleVideo = new SimpleVideo(
      this.root,
      this.mediaDataForSimpleVideo(),
      this.attributes,
      otherEngine,
    );

    this.setupQualityChangeTracking();

    this.loadAndParseMaster().then((manifest) => {
      this.manifest = manifest;
    });

    this.unbinds.push(
      elemBind(this.getMediaElement(), 'loadedmetadata', () => {
        if (this.attributes.audioPreference) {
          this.getAudioTracks().then((tracks) => {
            const track = tracks.find(
              (track) =>
                track.language === this.attributes.audioPreference.lang &&
                track.characteristics === undefined,
            );

            this.changeAudioTrack(track.id);
          });
        }
      }),

      elemBind(this.getMediaElement(), 'loadeddata', () => {
        // #firstFragProgramDateTime is used by live to calculate the time of current video
        // taking into consideration when the live stream was actually started
        if (!this.#firstFragProgramDateTime) {
          this.#firstFragProgramDateTime = this.getMediaElement().getStartDate();
        }

        // the `audioTracks` might not load during the constructor call, so
        // wait for loaded data before adding that binding
        this.unbinds.push(
          elemBind(this.getMediaElement().audioTracks, 'change', () => {
            this.trigger('audiostreamchange');
          }),
        );
      }),
    );

    if (this.attributes.liveMedia) {
      if (
        this.mediaData.liveStreamEventDetails?.startedAt &&
        this.mediaData.liveStreamEventDetails?.m3u8RenditionProgramTime
      ) {
        this.handleInitialLiveStreamEventDetailsChange(mediaData);
      }

      const pollForLiveStreamChange = () => {
        fetchMediaData(this.attributes.hashedId, {
          embedHost: this.attributes.embedHost,
          skipCache: true,
          bypassOriginShield: true,
        }).then((mediaData) => {
          if (this.canLiveStreamStart(mediaData)) {
            const { startedAt, m3u8RenditionProgramTime } = mediaData.liveStreamEventDetails;

            // the first time we go live these will be undefined and we need to set them for future comparisons
            if (
              this.#liveStreamStartedAt === undefined &&
              this.#liveStreamM3u8RenditionProgramTime === undefined
            ) {
              this.handleInitialLiveStreamEventDetailsChange(mediaData);
            } else if (
              // if the stream has restarted we need to reload the new source
              startedAt !== this.#liveStreamStartedAt &&
              m3u8RenditionProgramTime !== this.#liveStreamM3u8RenditionProgramTime
            ) {
              this.onMediaDataChanged(mediaData);
              this.trigger('livestream-mediadata-changed', mediaData);
            }
          }

          this._pollForLiveStreamTimeout = setTimeout(pollForLiveStreamChange, 1000);
        });
      };

      pollForLiveStreamChange();
    } else {
      this.setMediaSrc();
    }
  }

  async loadAndParseMaster() {
    // Step 1: Fetch the raw M3U8
    if (this.manifest) {
      return Promise.resolve(this.manifest);
    }

    const url = HlsAssets.multivariantM3u8Url(this);
    const res = await fetch(url, { mode: 'cors' });
    if (!res.ok) {
      console.error(`HTTP ${res.status} loading manifest`);
      return null;
    }

    const text = await res.text();

    // Step 2: Parse into an object
    // We need to set the options to silent to avoid console warnings
    // about AD audio tracks and alt audio tracks having the same name.
    // Can be removed once we update HLS manifest creation.
    setOptionsHls({ silent: true });
    const manifest = parseHls(text);

    return manifest;
  }

  handleInitialLiveStreamEventDetailsChange(mediaData) {
    this.#liveStreamStartedAt = mediaData.liveStreamEventDetails.startedAt;
    this.#liveStreamM3u8RenditionProgramTime =
      mediaData.liveStreamEventDetails.m3u8RenditionProgramTime;

    this.onMediaDataChanged(mediaData);
    this.setMediaSrc(mediaData);
    // do this on the next tick, otherwise on iOS `play`
    // may be rejected since setting the src on iOS can be
    // slower than our event bindings
    setTimeout(() => {
      this.trigger('livestreamready');
    }, 0);
  }

  addTextTracks() {
    // HLS tracks are in the manifest and should not be manually added
  }

  canLiveStreamStart(mediaData) {
    return (
      mediaData.liveStreamEventDetails?.startedAt &&
      mediaData.liveStreamEventDetails?.m3u8RenditionProgramTime &&
      mediaData.liveStreamEventDetails?.manifestUrl
    );
  }

  changeAudioTrack(id) {
    // there's a scenario where in `beforeplay` state, we can't actually set the audio track
    // but we can queue it up so that on `play` we do switch the desired audio track.
    // We do however want to resolve immmediately, so that we can update the UI to
    // reflect the selection
    const videoElem = this.getMediaElement();
    const audioTrackId = Number(id);
    return new Promise((resolve) => {
      if (
        videoElem.audioTracks &&
        videoElem.audioTracks[audioTrackId] &&
        this._currentAudioTrackId !== audioTrackId
      ) {
        const audioStreamChangeUnbind = this.bind('audiostreamchange', () => {
          audioStreamChangeUnbind();
          resolve();
        });

        this.updateEnabledStateForAudioTracks(audioTrackId);
        this._currentAudioTrackId = audioTrackId;
      } else {
        if (audioTrackId !== 0 && this.getPlaybackMode() === 'beforeplay') {
          const unbindPlay = this.simpleVideo.bind('playing', () => {
            // safari just straight up doesn't assign any tracks as enabled if
            // done too soon
            setTimeout(() => {
              this.updateEnabledStateForAudioTracks(this._currentAudioTrackId);
              unbindPlay();
            }, 100);
          });
        }

        this._currentAudioTrackId = audioTrackId;
        this.trigger('audiostreamchange');
        resolve();
      }
    });
  }

  loadSource() {
    return this.changeStreamBasedOnState();
  }

  changeStreamBasedOnState(options = {}) {
    if (this.getPlaybackMode() === 'beforeplay') {
      this.changeStreamWithoutLoad(HlsAssets.adaptiveAsset(this, options));
    } else {
      this.changeStream(HlsAssets.adaptiveAsset(this, options));
    }
  }

  changeQuality(quality) {
    if (quality === 'auto') {
      this.changeStreamBasedOnState();
      return Promise.resolve();
    }
    return this.changeToSingleHlsStream(quality);
  }

  changeToSingleHlsStream(quality) {
    let videoAssets;

    if (quality.toString().includes('HDR')) {
      videoAssets = HlsAssets.allMp4VideoAssets(this.allAssets).filter(
        (asset) => asset.type === 'hdr_video',
      );
    } else {
      videoAssets = HlsAssets.allMp4VideoAssets(this.allAssets);
    }

    // grab just the closest asset that matches the new selected quality
    const matchingQualityAssets = [findClosestAssetByQuality(videoAssets, quality)];
    const asset = matchingQualityAssets[0];
    const { height, width } = asset;
    // build a new master playlist m3u8 playlist with the selected quality as the only
    // video asset
    const masterM3u8DataUri = HlsAssets.multivariantM3u8Url(this, {
      qualityMin: Math.min(height || 0, width || 0), // min the height or width to account for vertical videos
      qualityMax: quality,
    });

    const m3u8Asset = {
      ...asset,
      url: masterM3u8DataUri,
    };

    return this.changeStream(m3u8Asset);
  }

  destroy() {
    this.unbinds.forEach((e) => {
      if (typeof e === 'function') {
        e();
      }
    });

    clearTimeout(this._pollForLiveStreamTimeout);
    super.destroy();
    return this.simpleVideo.destroy();
  }

  endOfLiveBufferTime() {
    const videoElem = this.getMediaElement();
    const buffered = videoElem.buffered;

    return videoElem.buffered.end(buffered.length - 1);
  }

  // hls.js and safari have different mechanisms for saying if a audio track is on.
  // we align them both through an `isSelected` field on the track
  async getAudioTracks() {
    return this.loadAndParseMaster().then((manifest) => {
      return manifest.variants[0].audio.map((track, index) => {
        const isEnabled = this._currentAudioTrackId === index;

        return {
          ...track,
          id: index,
          isSelected: isEnabled,
          enabled: isEnabled,
          label: track.name,
        };
      });
    });
  }

  async getCurrentAudioTrack() {
    const tracks = await this.getAudioTracks();
    let enabledTrack = tracks[0];

    for (let i = 0; i < tracks.length; i++) {
      if (tracks[i].enabled) {
        enabledTrack = tracks[i];
        break;
      }
    }

    return enabledTrack;
  }

  // In all cases except for Live, we should deferring to the Simple Video's `getCurrentTime` method.
  // For Live, we want the current time to be the time since the Live stream began, not since when a viewer landed on
  // the page; which is the default behavior because we're using a Sliding Window HLS playlist format.
  // In order to calculate the time since the Live Stream began, we do a little math.
  // We have the ProgramDateTime value of the very first fragment in the HLS Playlist, provided to us
  // via mediaData. We then get the ProgramDateTime of the first fragment this viewer received via Safari's
  // `getStartDate()` method. We can take the difference between those two times + the video's `currentTime` and get the total time
  // the stream has been active and the viewer's position.
  getCurrentTime() {
    // If we're not a live media or we don't have all this information, we should just use SimpleVideo.getCurrentTime
    if (
      !this.attributes.liveMedia ||
      !this.#firstFragProgramDateTime ||
      !this.mediaData.liveStreamEventDetails?.m3u8RenditionProgramTime
    ) {
      return this.simpleVideo.getCurrentTime();
    }

    const firstFragTime = new Date(this.#firstFragProgramDateTime).getTime();

    const liveStartTime = new Date(
      this.mediaData.liveStreamEventDetails.m3u8RenditionProgramTime,
    ).getTime();

    const diffBetweenFirstTimeAndStartTimeInSeconds = (firstFragTime - liveStartTime) / 1000;

    return diffBetweenFirstTimeAndStartTimeInSeconds + this.simpleVideo.getCurrentTime();
  }

  getCurrentQuality() {
    return this.selectedQuality();
  }

  mediaDataForSimpleVideo() {
    return mediaDataWithAssets(this.mediaData, this.selectableAssets());
  }

  onMediaDataChanged(mediaData) {
    this.mediaData = mediaData;
  }

  onReady() {
    if (!this.attributes.liveMedia) {
      return this.simpleVideo.onReady();
    }

    if (this.readyPromise) {
      return this.readyPromise;
    }

    this.readyPromise = this.simpleVideo.onReady().then(() => {
      return new Promise((resolve) => {
        if (this.#liveStreamStartedAt && this.#liveStreamM3u8RenditionProgramTime) {
          resolve();
        } else {
          this.unbinds.push(
            this.bind('livestreamready', () => {
              resolve();
            }),
          );
        }
      });
    });

    return this.readyPromise;
  }

  play(options) {
    // it is important that we do this before issuing the play command for
    // analytics purposes. We want the event sequence to be `seek`, then `unpause`.
    // if we wait until the play is resolved to seek to edge, we get `unpause` then `seek`
    // and the current time of the events is no what analytics would like/expect
    if (this.attributes.liveMedia) {
      count('live_stream/play', 1, { hashed_id: this.attributes.hashedId });
      return this.setCurrentTimeToLiveEdge().then(() => {
        return this.simpleVideo.play(options);
      });
    }

    return this.simpleVideo.play(options);
  }

  qualityForAsset(asset) {
    if (asset.slug === 'Auto') {
      return 'auto';
    }

    if (asset.type === 'hdr_video') {
      return `${numericSizeSnapped(asset.width, asset.height)} (HDR)`;
    }

    return numericSizeSnapped(asset.width, asset.height);
  }

  qualityMax(qualityMax) {
    this.attributes.qualityMax = qualityMax;
    this.changeStreamBasedOnState();
  }

  qualityMin(qualityMin) {
    this.attributes.qualityMin = qualityMin;
    this.changeStreamBasedOnState();
  }

  removeTextTracks() {
    // HLS tracks are in the manifest and should not be removed
  }

  selectableAssets() {
    return selectable(this);
  }

  selectableQualities() {
    return this.selectableAssets().map((asset) => {
      return this.qualityForAsset(asset);
    });
  }

  selectedQuality() {
    return this.qualityForAsset(this.selectedAsset());
  }

  setCurrentTimeToLiveEdge() {
    if (this.getPlaybackMode() !== 'beforeplay') {
      const endOfBufferTime = this.endOfLiveBufferTime();
      return this.seek(endOfBufferTime);
    }

    return Promise.resolve();
  }

  setMediaSrc(mediaData) {
    const data = mediaData || this.mediaData;

    if (this.attributes.liveMedia) {
      if (this.canLiveStreamStart(data)) {
        this.getMediaElement().src = data.liveStreamEventDetails.manifestUrl;
      } else {
        console.error('Live stream not ready to start');
      }
    } else {
      this.getMediaElement().src = HlsAssets.multivariantM3u8Url(this);
    }
  }

  // This is used so we can send bitrate information to Mux. In native we don't have a way to detect
  // auto quality shifts, so we monitor the video element's width. If it changes, we trigger the event
  setupQualityChangeTracking() {
    globalEventLoop.add(`${this.uuid}.events`, 300, () => {
      const currentVideoWidth = this.getMediaElement().videoWidth;
      if (this.videoWidth !== currentVideoWidth) {
        let assets = withinQualityRange(
          HlsAssets.allMp4VideoAssets(this.allAssets),
          currentVideoWidth,
          currentVideoWidth,
        );

        if (assets.length === 0) {
          assets = nearestOutsideRange(
            HlsAssets.allMp4VideoAssets(this.allAssets),
            currentVideoWidth,
            currentVideoWidth,
          );
        }

        const asset = assets[0];

        this.trigger('hlslevelswitched', { asset });
        this.videoWidth = currentVideoWidth;
      }
    });
  }

  updateEnabledStateForAudioTracks(trackIdToSetAsEnabled) {
    const videoElem = this.getMediaElement();
    for (let i = 0; i < videoElem.audioTracks.length; i++) {
      videoElem.audioTracks[i].enabled = Boolean(i === trackIdToSetAsEnabled);
    }
  }

  updateStartPosition(time) {
    this.attributes.startPosition = time;
    this.changeStreamBasedOnState({ startPosition: time });
  }

  usingInstantHls() {
    return (
      (this.attributes.forceInstantHls || !this.mediaData.playableWithoutInstantHls) &&
      this.mediaData.instantHlsAssetsReady
    );
  }
}

delegatePublicMethods(NativeHlsVideo.prototype, function () {
  return this.simpleVideo;
});

defineEngine('NativeHlsVideo', NativeHlsVideo);

export default NativeHlsVideo;
